import React from 'react';
import { motion } from 'framer-motion';
import { User, KeyRound, Check, Copy, ShieldCheck } from 'lucide-react';
import { DjayAvatar } from '../../../mascot';
import { ConfettiCelebration } from '../ConfettiCelebration';
import { LINK_CODE_LENGTH } from '../../logic/auth';
type Role = 'learner' | 'parent' | 'teacher';
interface RegisterSuccessStepProps {
  role: Role;
  name: string;
  generatedUsername: string;
  generatedLinkCode: string;
  onCopyLinkCode: () => void;
  copied: boolean;
  /** Leeg wanneer het opslaan mislukte — dan tonen we bewust geen code. */
  generatedRecoveryCode?: string;
  onCopyRecoveryCode?: () => void;
  recoveryCopied?: boolean;
  /**
   * Aanwezig wanneer er géén aftelklok loopt en het kind zelf bevestigt.
   * Zie RegisterScreen: een herstelcode is eenmalig en mag niet vanzelf van
   * het scherm verdwijnen.
   */
  onContinue?: () => void;
}
export function RegisterSuccessStep({
  role,
  name,
  generatedUsername,
  generatedLinkCode,
  onCopyLinkCode,
  copied,
  generatedRecoveryCode,
  onCopyRecoveryCode,
  recoveryCopied,
  onContinue
}: RegisterSuccessStepProps) {
  return <motion.div key="step-success" initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.4
  }} className="text-center py-4 relative" data-id="element-472" data-mp="d2xmlk5mk3ek">
      
      <ConfettiCelebration data-id="element-473" data-mp="n1wu2zenlszz" />

      <motion.h2 className="text-2xl font-black text-slate-900 mb-2" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-474" data-mp="mxze2mndhhkb">
        
        Welkom bij Meester djay! 🎉
      </motion.h2>

      {role === 'learner' ? <motion.div className="space-y-4" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.45
    }} data-id="element-475" data-mp="10ttj2ul0zms5">
        
          {/*
           Stond hier als "Bewaar deze gegevens goed", boven gebruikersnaam ÉN
           koppelcode. Dat klopt maar voor één van de twee, en de fout is niet
           onschuldig: een kind dat de koppelcode opschrijft "voor later" geeft
           hem maanden daarna aan oma — en dan werkt hij niet meer, want de
           code is vervangbaar en wordt binnenkort ook tijdelijk (7 dagen, zie
           SHORT_CODE_SECURITY §5d). Oma en kind concluderen dan allebei dat
           zíj iets fout doen.
            Wat je écht moet bewaren is de herstelcode, en die staat hieronder
           in een eigen blok dat dat zelf zegt.
           */}
          <p className="text-slate-600 font-medium" data-id="element-476" data-mp="3pkbz9l1ojsb7">
            Dit is je inlog:
          </p>
          <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 text-left space-y-3" data-id="element-477" data-mp="7e7gdo3bcfpd">
            <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-orange-100" data-id="element-478" data-mp="3c2puy85vz6y">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-479" data-mp="296c3mm9vaotr">
                <User className="w-5 h-5" data-id="element-480" data-mp="255ighv491e1x" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-481" data-mp="1uzikrc90znwv">
                <div className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-0.5" data-id="element-482" data-mp="1v1vaibl0z2kh">
                  Gebruikersnaam
                </div>
                <div className="font-mono font-bold text-lg text-orange-900 truncate" data-id="element-483" data-mp="1l8wck33n1zo7">
                  {generatedUsername}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-orange-100" data-id="element-484" data-mp="1bb9q8vk0f5u7">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-485" data-mp="1gb4ib2fiu1gu">
                <KeyRound className="w-5 h-5" data-id="element-486" data-mp="12co2mlzol0mk" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-487" data-mp="179eib87kpu7g">
                <div className="text-[10px] font-bold text-orange-400 uppercase tracking-wider mb-0.5" data-id="element-488" data-mp="17cafgublkeuu">
                  Koppelcode
                </div>
                <div className="font-mono font-black text-xl tracking-[0.2em] text-orange-900 truncate" data-id="element-489" data-mp="31oh00jqds7zt">
                  {generatedLinkCode}
                </div>
              </div>
              <button onClick={onCopyLinkCode} className="p-2 rounded-lg hover:bg-orange-50 text-orange-500 transition-colors flex-shrink-0" title="Kopieer code" data-id="element-490" data-mp="wmd2n2ivpy0k">
              
                {copied ? <Check className="w-5 h-5 text-emerald-500" data-id="element-491" data-mp="nmghhavp7ha9" /> : <Copy className="w-5 h-5" data-id="element-492" data-mp="nln8025cudof" />}
              </button>
            </div>
            <p className="text-xs text-orange-700 mt-2 font-medium leading-relaxed" data-id="element-493" data-mp="io562vpr8ypx">
              Geef deze code aan je ouder, dan kan die je voortgang zien. Je
              hoeft hem niet op te schrijven — hij staat altijd in je
              instellingen, en je kunt daar ook een nieuwe maken als iemand hem
              heeft die hem niet mag hebben.
            </p>
          </div>

          {/*
           De herstelcode staat bewust in een eigen blok met een andere kleur.
           Hij lijkt op de koppelcode maar doet het tegenovergestelde: de
           koppelcode DEEL je, de herstelcode houd je juist voor jezelf. Twee
           codes met dezelfde opmaak naast elkaar is vragen om een kind dat de
           verkeerde doorgeeft.
           */}
          {generatedRecoveryCode ? <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 text-left space-y-3" data-id="element-494" data-mp="2flazrpcgqzrh">
              <div className="flex items-start gap-2" data-id="element-495" data-mp="2kkvz40vjpa4q">
                <ShieldCheck className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" data-id="element-496" data-mp="2ki9r604qsslz" />
                <div data-id="element-497" data-mp="1nw7se0pa59am">
                  <p className="text-sm font-bold text-sky-900" data-id="element-498" data-mp="1rvyevs7rx39c">
                    Je geheime herstelcode
                  </p>
                  <p className="text-xs text-sky-700 font-medium" data-id="element-499" data-mp="1wut7flvjm66n">
                    Schrijf hem over en bewaar hem thuis. Hiermee kom je weer
                    binnen als je je wachtwoord vergeet. Je ziet hem maar één
                    keer.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-sky-200" data-id="element-500" data-mp="194fezu91q5bj">
                <div className="flex-1 min-w-0 font-mono font-bold text-sky-900 leading-relaxed break-words" data-id="element-501" data-mp="2cfct0168ulaz">
                  {generatedRecoveryCode}
                </div>
                <button type="button" onClick={onCopyRecoveryCode} className="p-2 rounded-lg hover:bg-sky-50 text-sky-600 transition-colors flex-shrink-0" aria-label="Kopieer herstelcode" data-id="element-502" data-mp="2l97wl4rpw3oi">
              
                  {recoveryCopied ? <Check className="w-5 h-5 text-emerald-500" data-id="element-503" data-mp="1tijvfsq1myau" /> : <Copy className="w-5 h-5" data-id="element-504" data-mp="1tjd4x1ge01wo" />}
                </button>
              </div>
              <p className="text-xs text-sky-700 font-medium" data-id="element-505" data-mp="1okih1l3kbn5p">
                🔒 Deel deze code met niemand — ook niet met een vriend.
              </p>
            </div> : null}

          {onContinue ? <button type="button" onClick={onContinue} className="w-full rounded-xl bg-orange-500 px-4 py-3 font-bold text-white hover:bg-orange-600 transition-colors" data-id="element-506" data-mp="1xl8708656jbi">
          
              {generatedRecoveryCode ? 'Ik heb mijn herstelcode opgeschreven — ga verder' : 'Ga verder'}
            </button> : <p className="text-sm text-slate-400 font-medium animate-pulse" data-id="element-507" data-mp="3hdzvd9vedobg">
              Je wordt zo ingelogd...
            </p>}
        </motion.div> : role === 'teacher' ? <motion.div className="space-y-4" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.45
    }} data-id="element-508" data-mp="37i5984rytb3x">
        
          <p className="text-slate-600 font-medium" data-id="element-509" data-mp="404ve8zqabie">
            Welkom, meester/juf! Je docentaccount is klaar.
          </p>
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4" data-id="element-510" data-mp="8y6hl2sgaqy3">
            <p className="text-sm text-indigo-800 font-medium" data-id="element-511" data-mp="3qey2i114uby9">
              In het docentportaal kun je leerlingen toevoegen aan je klas.
            </p>
          </div>
          <p className="text-sm text-slate-400 font-medium animate-pulse" data-id="element-512" data-mp="mzisgzsr82d4">
            Naar je dashboard...
          </p>
        </motion.div> : <motion.div className="space-y-4" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.45
    }} data-id="element-513" data-mp="rqr504pds3ui">
        
          <p className="text-slate-600 font-medium" data-id="element-514" data-mp="2j1chaiy5ngxm">
            Welkom! Je ouderaccount is aangemaakt.
          </p>
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4" data-id="element-515" data-mp="2e43ug522aoq1">
            <p className="text-sm text-emerald-800 font-medium" data-id="element-516" data-mp="2a2qozw2vsnjn">
              {/*
               Stond eerder als "6-cijferige koppelcode", daarna als "6 tekens".
               Een koppelcode is geen getal maar letters én cijfers uit een
               alfabet zonder 0, 1, I, L, O en S — juist gekozen zodat
               voorlezen door de telefoon niet misgaat. En de lengte komt uit
               de constante: hij is inmiddels 8, en een los getal in deze zin
               laat een ouder op het verkeerde aantal tellen.
               */}
              Koppel je kind(eren) met hun koppelcode van {LINK_CODE_LENGTH}{' '}
              tekens.
            </p>
          </div>
          <p className="text-sm text-slate-400 font-medium animate-pulse" data-id="element-517" data-mp="3t0x09zso75xh">
            Naar het ouderportaal...
          </p>
        </motion.div>}
    </motion.div>;
}